import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
  /**
   * When true, render with the large pastel-circle illustration backdrop.
   * Used on the main empty states for Today / Conversations / Leads — adds
   * the calm "you're winning" feel of Linear / Insta empty pages instead of
   * a sad face.
   */
  variant?: 'plain' | 'illustrated';
}

/**
 * EmptyState — calm, illustrated empty pages instead of sad-face placeholders.
 *
 * The `illustrated` variant wraps the icon in a soft SS-blue pastel circle,
 * adds a subtle scale-in animation, and centers the copy with breathing
 * room. Same energy as Linear's "You're all caught up" page or Insta's
 * "When friends post, you'll see them here."
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  variant = 'illustrated',
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center px-8 py-20 text-center animate-ss-fade-in">
      {icon ? (
        variant === 'illustrated' ? (
          <div className="relative mb-5 inline-flex h-24 w-24 items-center justify-center">
            {/* outer pastel halo */}
            <div className="absolute inset-0 rounded-full bg-ss-blue-light dark:bg-ss-blue/15" />
            {/* inner solid circle */}
            <div className="absolute inset-3 rounded-full bg-white shadow-ss-sm dark:bg-[#1E293B]" />
            <div className="relative text-ss-blue animate-ss-scale-in">
              {icon}
            </div>
          </div>
        ) : (
          <div className="mb-3 text-ink-300 dark:text-[#475569]">{icon}</div>
        )
      ) : null}
      <h2 className="text-[17px] font-semibold tracking-tight text-ink-900 dark:text-[#E2E8F0]">
        {title}
      </h2>
      {body ? (
        <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-ink-500 dark:text-[#94A3B8]">
          {body}
        </p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
